import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import { getTrackingAttrs as getTrackingAttrsUtil } from '@Containers/PatentFamilyClustering/utils';
import useExpandPatentFamilyGroupFromBreadcrumb from '@Containers/PatentFamilyClustering/hooks/useExpandPatentFamilyGroupFromBreadcrumb';
import {
  groupTitle as groupTitleSelector,
  groupLanguage as groupLanguageSelector,
  groupOffices as groupOfficesSelector,
  groupCount as groupCountSelector,
  pageNumber as pageNumberSelector,
  patentFamilyMembers as patentFamilyMembersSelector,
  isExpanded as isExpandedSelector,
  isLoading as isLoadingSelector,
} from './selectors';
import {
  expandPatentFamilyGroup as expandPatentFamilyGroupAction,
  collapsePatentFamilyGroup as collapsePatentFamilyGroupAction,
  setPageNumber as setPageNumberAction,
  viewFullPatentFamily as viewFullPatentFamilyAction,
} from './actions';
import PatentFamilyGroup from './PatentFamilyGroup';

export function PatentFamilyGroupContainer({
  // Own props
  document,
  metadata,
  zoom,
  // Selectors
  groupTitle,
  groupLanguage,
  groupOffices,
  groupCount,
  pageNumber,
  isExpanded,
  isLoading,
  patentFamilyMembers,
  // Actions
  expandPatentFamilyGroup,
  collapsePatentFamilyGroup,
  setPageNumber,
  viewFullPatentFamily,
}) {
  useExpandPatentFamilyGroupFromBreadcrumb({ document, metadata }, () => {
    expandPatentFamilyGroup({ document, metadata });
  });

  const getTrackingAttrs = useMemo(() => getTrackingAttrsUtil({ document, metadata }), [document, metadata]);

  return (
    <PatentFamilyGroup
      document={document}
      metadata={metadata}
      zoom={zoom}
      groupTitle={groupTitle}
      groupLanguage={groupLanguage}
      groupOffices={groupOffices}
      groupCount={groupCount}
      pageNumber={pageNumber}
      isExpanded={isExpanded}
      isLoading={isLoading}
      patentFamilyMembers={patentFamilyMembers}
      expandPatentFamilyGroup={expandPatentFamilyGroup}
      collapsePatentFamilyGroup={collapsePatentFamilyGroup}
      setPageNumber={setPageNumber}
      viewFullPatentFamily={viewFullPatentFamily}
      getTrackingAttrs={getTrackingAttrs}
    />
  );
}

PatentFamilyGroupContainer.propTypes = {
  // Own props
  metadata: MetadataPropTypesShape,
  document: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  // Selectors
  groupTitle: PropTypes.string,
  groupLanguage: PropTypes.string,
  groupOffices: PropTypes.array,
  groupCount: PropTypes.string,
  pageNumber: PropTypes.number,
  isExpanded: PropTypes.bool,
  isLoading: PropTypes.bool,
  patentFamilyMembers: PropTypes.array,
  // Actions
  expandPatentFamilyGroup: PropTypes.func.isRequired,
  collapsePatentFamilyGroup: PropTypes.func.isRequired,
  setPageNumber: PropTypes.func.isRequired,
  viewFullPatentFamily: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  groupTitle: groupTitleSelector(state, ownProps),
  groupLanguage: groupLanguageSelector(state, ownProps),
  groupOffices: groupOfficesSelector(state, ownProps),
  groupCount: groupCountSelector(state, ownProps),
  pageNumber: pageNumberSelector(state, ownProps),
  patentFamilyMembers: patentFamilyMembersSelector(state, ownProps),
  isExpanded: isExpandedSelector(state, ownProps),
  isLoading: isLoadingSelector(state, ownProps),
});

const mapDispatchToProps = {
  expandPatentFamilyGroup: expandPatentFamilyGroupAction,
  collapsePatentFamilyGroup: collapsePatentFamilyGroupAction,
  setPageNumber: setPageNumberAction,
  viewFullPatentFamily: viewFullPatentFamilyAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(PatentFamilyGroupContainer);
